import { getCurrencySymbol } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DealsService } from '@core/deals/deals.service';
import {
  AppConfigService,
  HttpService,
  TypedDictionary,
  WindowService,
} from '@core/index';
import { TextContentService } from '@core/text-content/text-content.service';
import { BodyText } from '@dynamic-components/body-text-container/types';
import { getMacAreaForAirport } from '@dynamic-components/booker-v2/shared/city-pair-selector/city-pair-selector.utils';
import { BookerResponse } from '@shared/booker';
import { BookerAirService } from '@shared/booker/bookers/booker-air/booker-air.service';
import { DealLegalModalDialogComponent } from '@shared/deal-legal-modal-dialog';
import { FlightDeal, RecentAirSearch } from '@shared/types';
import { filterDealsByRestrictDestinations } from '@shared/ui/utils/deal-utils/filter-deals-by-restrict-destinations';
import { getDefaultAllowedOrigin } from '@shared/ui/utils/deal-utils/get-default-allowed-origin';
import { BookerFacade } from '@store/booker/booker.facade';
import { OriginsApi } from '@store/origins/api';
import { OriginsFacade } from '@store/origins/origins.facade';
import { Airport } from '@store/origins/types';
import {
  JbDialogService,
  JbOfferBlockValueTypeEnum,
} from 'jb-component-library';
import { combineLatest, Observable, of } from 'rxjs';
import {
  catchError,
  filter,
  map,
  share,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';

import { FlightBlock, Theme } from './flight-block.type';

@Component({
  selector: 'jb-isolated-flight-block-container',
  templateUrl: './isolated-flight-block-container.component.html',
  styleUrls: ['./isolated-flight-block-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IsolatedFlightBlockContainerComponent implements OnInit {
  private _destroy = inject(DestroyRef);

  @Input() theme: Theme;
  @Input() flightBlock: FlightBlock;
  @Input() serviceError: BodyText[];
  @Input() noResults: BodyText[];
  @Input() requestMinDeals: boolean;
  @Input() flightsGoTo: string;
  @Input() pageWithBooker: string;

  bookerData$: Observable<BookerResponse> = this.textContentService
    .getJsonAsset('booker-response-text')
    .pipe(share());

  flightDeals: FlightDeal[] = [];

  isMobile: Observable<boolean> = this.windowService.isMobile;
  destinationAirports;
  countriesWithAirports;
  enableLazyLoadImage: boolean;
  placeholderParams: string;
  isLoading = true;
  constructor(
    public dialogService: JbDialogService,
    private appConfigService: AppConfigService,
    private dealsService: DealsService,
    private originsFacade: OriginsFacade,
    private bookerFacade: BookerFacade,
    private windowService: WindowService,
    private cdRef: ChangeDetectorRef,
    private originsApi: OriginsApi,
    private bookerAirService: BookerAirService,
    private textContentService: TextContentService,
  ) {}

  ngOnInit() {
    this.enableLazyLoadImage = this.appConfigService.lazyLoadImages;
    this.placeholderParams =
      this.appConfigService.paths[0].placeholderImageParams;

    this.bookerFacade.allFilteredAirports$
      .pipe(
        takeUntilDestroyed(this._destroy),
        filter(allAirports => !!allAirports?.length),
        take(1),
      )
      .subscribe(countriesWithAirports => {
        this.countriesWithAirports = countriesWithAirports;
        this.bindOriginControlValueOnLoad();
      });
  }

  getDeals(selectedOrigin: string): void {
    this.isLoading = true;
    const offerBlock = this.flightBlock.offerBlock?.[0];
    const isPointDeals =
      offerBlock?.valueType === JbOfferBlockValueTypeEnum.points;
    const requestDealCount =
      !this.flightBlock.results?.[0]?.restrict && this.requestMinDeals
        ? this.flightBlock.results[0].maxDisplayed
        : 100;

    const restrictDestinations = this.flightBlock.results?.[0]?.restrict;

    // Conditional observable for destination airports based on restrictions
    // If restrictions are present, use an empty array to signify no destinations
    const destinationAirports$ = restrictDestinations
      ? of([])
      : this.originsApi
          .getDestinationAirportsFromOrigin(selectedOrigin, 'DEALS')
          .pipe(
            filter(HttpService.isSuccessful),
            filter(airportsResponse => !!airportsResponse.response?.length),
            map(airportsResponse => airportsResponse.response),
            take(1),
            catchError(() => of([])),
          );

    combineLatest([
      this.dealsService
        .getFlightsDeals(selectedOrigin, isPointDeals, requestDealCount)
        .pipe(catchError(() => of(undefined))),
      destinationAirports$,
    ])
      .pipe(
        takeUntilDestroyed(this._destroy),
        withLatestFrom(this.isMobile, this.originsFacade.airports),
      )
      .subscribe(([[deals, destinationAirports], isMobile, allAirports]) => {
        this.destinationAirports = destinationAirports;
        this.filterDeals(
          deals,
          selectedOrigin,
          isPointDeals,
          isMobile,
          allAirports,
        );
        this.isLoading = false;
      });
  }

  filterDeals(
    deals: FlightDeal[] | undefined,
    selectedOrigin: string,
    isPointDeals: boolean,
    isMobile: boolean,
    allAirports: Airport[],
  ) {
    if (deals) {
      const restrictDestinations = this.flightBlock.results?.[0]?.restrict;
      let flightDeals: FlightDeal[] = deals;

      if (restrictDestinations && restrictDestinations.length) {
        flightDeals = filterDealsByRestrictDestinations(
          flightDeals,
          restrictDestinations,
          this.flightBlock.results[0].maxDisplayed,
        );
      } else {
        flightDeals = flightDeals.slice(
          0,
          this.flightBlock.results[0].maxDisplayed,
        );
      }

      if (flightDeals.length) {
        this.originsFacade
          .getFullAirportFromCode(selectedOrigin)
          .pipe(take(1))
          .subscribe(origin => {
            if (!this.requestMinDeals && origin.cc) {
              // When origin is MAC, only show the best deal for each destination
              this.flightDeals = this.pluckBestDealForEachDestination(
                this.flightDeals,
              );
              this.isLoading = false;
            }
          });
      }

      if (
        flightDeals.length === 0 &&
        selectedOrigin !== this.flightBlock.origins[0].default
      ) {
        this.getDeals(this.flightBlock.origins[0].default);
      } else {
        this.isLoading = false;
      }
      this.flightDeals = flightDeals.map(flight => ({
        ...flight,
        url: this.constructFlightUrl(
          flight,
          isPointDeals,
          isMobile,
          allAirports,
        ),
        image: `${this.appConfigService.magnoliaUrl}/dam${this.appConfigService.paths[0]?.flightBlockImagePrefix}/${flight.destinationAirportCode}.jpg`,
        title: flight.to.substring(0, flight.to.length - 6), // to strip out last 6 characters containing space & airport code, eg: ' (YYZ)',
        currencySymbol: getCurrencySymbol(
          flight.currencyCode || 'USD',
          'narrow',
        ),
      }));
    } else {
      // undefined means there was error
      this.flightDeals = undefined;
      this.isLoading = false;
    }
    this.cdRef.markForCheck();
  }

  pluckBestDealForEachDestination(deals: FlightDeal[]): FlightDeal[] {
    const checklist = {};
    const bestDeals = [];
    deals.forEach(deal => {
      // FlightDeal[] is already sorted by price from low to high
      // Therefore the first deal of a destination is the lowest deal
      if (!checklist[deal.destinationAirportCode]) {
        checklist[deal.destinationAirportCode] = true;
        bestDeals.push(deal);
      }
    });
    return bestDeals;
  }

  openLegalDialog(deal: FlightDeal, block: FlightBlock, e?: Event): void {
    e?.stopPropagation();

    const componentRef = this.dialogService.openDialog(
      DealLegalModalDialogComponent,
    );
    componentRef.handleData(deal, { ...block.legalCopy[0] });
  }

  bindOriginControlValueOnLoad(): void {
    this.originsFacade.nearbyAirportCodes
      .pipe(
        filter(nearbyAirportCodes => nearbyAirportCodes != null),
        withLatestFrom(this.bookerFacade.recentAirSearches),
        switchMap(([nearbyAirportCodes, recentAirports]) =>
          this.originsFacade.airportsMap.pipe(
            filter(airportMap => !!airportMap),
            take(1),
            map(airportMap => [airportMap, nearbyAirportCodes, recentAirports]),
          ),
        ),
        take(1),
      )
      .subscribe(
        ([airportMap, nearbyAirportCodes, recentAirports]: [
          TypedDictionary<Airport>,
          string[],
          RecentAirSearch[],
        ]) => {
          const allowedOrigins = this.flightBlock.origins[0].restrict;
          const selectedOrigin = getDefaultAllowedOrigin(
            this.flightBlock.origins[0].default,
            allowedOrigins,
            nearbyAirportCodes.map(
              airportCode =>
                getMacAreaForAirport(airportCode, airportMap)?.code ||
                airportCode,
            ),
            recentAirports,
          );
          this.getDeals(selectedOrigin);
        },
      );
  }

  constructFlightUrl(
    flight,
    isPointDeals: boolean,
    isMobile: boolean,
    allAirports: Airport[],
  ) {
    const flightUrl = flight.url;
    // Example: https://www-int2.jetblue.com/best-fare-finder?Origin=EWR&Destination=BOS&Departure=2022-06-01
    const queryParamString = flightUrl.substring(flightUrl.indexOf('?') + 1);
    const urlRawString = flightUrl.substring(
      0,
      flightUrl.indexOf('best-fare-finder') - 1,
    );
    const pointsParam = isPointDeals ? `&fare=POINTS` : '';
    if (this.flightsGoTo === 'pageWithBooker') {
      return `${urlRawString}${this.pageWithBooker}?${queryParamString}${pointsParam}&roundtrip=false`;
    } else if (this.flightsGoTo === 'flightResults') {
      // in the queryPramString, make sure DESTINATION and ORIGIN is in upper case
      const QPS = queryParamString
        .replace(/destination=/gi, 'DESTINATION=')
        .replace(/origin=/gi, 'ORIGIN=');

      return this.bookerAirService.getFlightResultsUrl(
        QPS,
        isPointDeals,
        isMobile,
        // in case when destinationAirports is not available, use allAirports
        // now that we no longer rely on interline property in destination airports
        this.destinationAirports?.length
          ? this.destinationAirports
          : allAirports,
        this.countriesWithAirports,
      );
    } else {
      return `${flightUrl}${pointsParam}`;
    }
  }
}

export const isolatedFlightBlockContainerResolver = (_, data) => {
  return {
    theme: data.theme,
    flightBlock: data.flightBlock?.[0],
    serviceError: data.serviceError,
    noResults: data.noResults,
    flightsGoTo: data.flightsGoto,
    requestMinDeals: data.requestMinDeals === 'true',
    pageWithBooker: data.pageWithBooker,
  };
};
